import { useMemo } from 'react';
import {
  type DayOfWeekEnum,
  type GetHomeForYouPreferencesQuery,
  type PlayerFinderTimeOfDayEnum,
  type SkillLevelValuesEnum,
  SportsEnum,
  useGetHomeForYouPreferencesQuery,
} from 'types/generated/client';
import type { TimesByDay } from 'utils/shared/playerAvailability';
import { useViewer } from 'hooks/useViewer';
import { SELF_RATE_SKILL_LEVELS } from 'screens/FindOnboarding/types';
import type { VenueAddressWithCity } from 'screens/ListGame/types';
import { toVenueAddress } from 'screens/ListGame/utils';

type FollowedVenue = NonNullable<GetHomeForYouPreferencesQuery['venueFollows'][number]['venue']>;

/** The player's Find onboarding answers, for one sport. */
export interface ForYouPreferences {
  skillLevels: SkillLevelValuesEnum[];
  availabilityDays: DayOfWeekEnum[];
  timesByDay: TimesByDay;
  venues: VenueAddressWithCity[];
}

const skillLevelFor = (
  user: GetHomeForYouPreferencesQuery['usersByPk'],
  sport: SportsEnum,
): SkillLevelValuesEnum | null => {
  const id =
    sport === SportsEnum.Tennis
      ? user?.tennisSkillLevelId
      : sport === SportsEnum.Padel
      ? user?.padelSkillLevelId
      : user?.pickleballSkillLevelId;
  // skill_levels ids are the enum values ("INTERMEDIATE"…); legacy NO_RESPONSE /
  // DO_NOT_PLAY rows aren't selectable chips, so they must not seed the filter.
  const level = SELF_RATE_SKILL_LEVELS.find((option) => option.value === id);
  return level?.value ?? null;
};

/**
 * "For you" = the player's Find onboarding answers for the given sport, which
 * `buildForYouFilters` turns into a pre-filled filter set. Venue follows aren't
 * finder-exclusive, so every active follow counts as a preferred venue.
 */
export const useForYouPreferences = (sport: SportsEnum) => {
  const { userId } = useViewer();
  const { data, loading } = useGetHomeForYouPreferencesQuery({
    variables: { userId },
    skip: !userId,
    fetchPolicy: 'cache-and-network',
  });

  const preferences = useMemo<ForYouPreferences | null>(() => {
    if (!data) return null;

    const skill = skillLevelFor(data.usersByPk, sport);

    const timesByDay: TimesByDay = {};
    data.userPlayerFinderAvailability.forEach((row) => {
      const day = row.dayOfWeek as DayOfWeekEnum;
      const times = timesByDay[day] ?? [];
      if (!times.includes(row.timeOfDay as PlayerFinderTimeOfDayEnum)) {
        timesByDay[day] = [...times, row.timeOfDay as PlayerFinderTimeOfDayEnum];
      }
    });

    return {
      skillLevels: skill ? [skill] : [],
      availabilityDays: Object.keys(timesByDay) as DayOfWeekEnum[],
      timesByDay,
      venues: data.venueFollows
        .map((follow) => follow.venue)
        .filter((venue): venue is FollowedVenue => Boolean(venue))
        .map(toVenueAddress),
    };
  }, [data, sport]);

  return { preferences, loading };
};
